/**
 * The plant feed (piece 28, *The plant feed*; decisions 12500 to 12505; T3802):
 * the contract a plant publishes to, the ten settings the reader reads, and the
 * pure rules the reader applies, so the API, the card and the suites read one copy.
 *
 * 🔒 A message is one JSON object or an array of them; a reading carries `tag`,
 * `value`, `unit` where it has one, `at` and `clock`, and a fault `tag`, `code`,
 * `text`, `state`, `at` and `clock` (decision 12502). `at` is the event time on
 * the controller and `clock` the controller's own time when it sent.
 */

/** The four topics under `bms2/<siteKey>/` a site's broker user publishes on (decisions 12501, 12502). */
export const FEED_CHANNELS = ['readings', 'faults', 'status', 'events'] as const;
export type FeedChannel = (typeof FEED_CHANNELS)[number];
/** The two the HTTP push takes; the will and the reconnect event are MQTT's alone. */
export const FEED_PUSH_CHANNELS = ['readings', 'faults'] as const;
export type FeedPushChannel = (typeof FEED_PUSH_CHANNELS)[number];

/** The API's one subscription, every site's every channel (decision 12501). */
export const FEED_SUBSCRIPTION = 'bms2/+/+';
export const FEED_TOPIC_PREFIX = 'bms2';
/** The header the HTTP push carries the site's key in (decision 12502). */
export const FEED_KEY_HEADER = 'x-feed-key';
/** The caps on one message, either driver (decision 12502). */
export const FEED_MAX_ENTRIES = 1000;
export const FEED_MAX_BYTES = 256 * 1024;
/** A tag, a code and a fault's text are short names, never documents. */
export const FEED_MAX_TAG = 200;
export const FEED_MAX_TEXT = 500;

export function feedTopic(siteKey: string, channel: FeedChannel): string {
  return `${FEED_TOPIC_PREFIX}/${siteKey}/${channel}`;
}

/** The site key and channel a topic names, or null where it is not one of ours. */
export function feedTopicParts(topic: string): { siteKey: string; channel: FeedChannel } | null {
  const parts = topic.split('/');
  if (parts.length !== 3 || parts[0] !== FEED_TOPIC_PREFIX) return null;
  const channel = parts[2] as FeedChannel;
  if (!parts[1] || !(FEED_CHANNELS as readonly string[]).includes(channel)) return null;
  return { siteKey: parts[1], channel };
}

/** A site key: lower-case letters and digits, ten of them, so it reads cleanly in a topic and an address. */
export const SITE_KEY_PATTERN = /^[a-z0-9]{10}$/;
export function brokerUserOf(siteKey: string): string {
  return `site-${siteKey}`;
}

// ---- The settings (decisions 12502, 12504, 12505) ----

export const FEED_NOISE_MODES = ['Keep', 'DropEqual', 'DropWithinShare'] as const;
export type FeedNoiseMode = (typeof FEED_NOISE_MODES)[number];

export const FEED_NOISE_MODE_KEY = 'assets.feedNoiseMode';
export const FEED_NOISE_SHARE_KEY = 'assets.feedNoiseShare';
export const FEED_HEARTBEAT_MINUTES_KEY = 'assets.feedHeartbeatMinutes';
export const FEED_KEEP_DAYS_KEY = 'assets.feedKeepDays';
export const FEED_CLOCK_SKEW_WARN_MINUTES_KEY = 'assets.feedClockSkewWarnMinutes';
export const FEED_ALARM_REPEAT_COUNT_KEY = 'assets.feedAlarmRepeatCount';
export const FEED_ALARM_REPEAT_MINUTES_KEY = 'assets.feedAlarmRepeatMinutes';
export const FEED_ALARM_OPEN_MINUTES_KEY = 'assets.feedAlarmOpenMinutes';
export const FEED_SILENT_MINUTES_KEY = 'assets.feedSilentMinutes';
export const FEED_PUSH_PER_KEY_MINUTE_KEY = 'assets.feedPushPerKeyMinute';
export const FEED_PUSH_PER_ADDRESS_MINUTE_KEY = 'assets.feedPushPerAddressMinute';

export const PURGE_PLANT_FEED_JOB = 'purgePlantFeed';
export const PURGE_PLANT_FEED_HOUR_KEY = 'assets.feedPurgeRunHour';
export const PURGE_PLANT_FEED_DEFAULT_HOUR = 4;

export interface FeedSettings {
  noiseMode: FeedNoiseMode;
  /** Percent of the last kept value. */
  noiseShare: number;
  heartbeatMinutes: number;
  keepDays: number;
  clockSkewWarnMinutes: number;
  alarmRepeatCount: number;
  alarmRepeatMinutes: number;
  alarmOpenMinutes: number;
  silentMinutes: number;
  /** HTTP pushes a minute one site key may send. */
  pushPerKeyMinute: number;
  /** HTTP pushes a minute one source address may send. */
  pushPerAddressMinute: number;
}

/** The seeded values, the owner's L2c, L3a and L8b and the orchestrator's defaults of 2026-11-01. */
export const FEED_SETTING_DEFAULTS: FeedSettings = {
  noiseMode: 'DropEqual',
  noiseShare: 0.1,
  heartbeatMinutes: 15,
  keepDays: 90,
  clockSkewWarnMinutes: 5,
  alarmRepeatCount: 3,
  alarmRepeatMinutes: 60,
  alarmOpenMinutes: 30,
  silentMinutes: 30,
  pushPerKeyMinute: 120,
  pushPerAddressMinute: 600,
};

/** Each setting's key beside the field it fills, in the order the Live Data page will list them. */
export const FEED_SETTING_KEYS: Readonly<Record<keyof FeedSettings, string>> = {
  noiseMode: FEED_NOISE_MODE_KEY,
  noiseShare: FEED_NOISE_SHARE_KEY,
  heartbeatMinutes: FEED_HEARTBEAT_MINUTES_KEY,
  keepDays: FEED_KEEP_DAYS_KEY,
  clockSkewWarnMinutes: FEED_CLOCK_SKEW_WARN_MINUTES_KEY,
  alarmRepeatCount: FEED_ALARM_REPEAT_COUNT_KEY,
  alarmRepeatMinutes: FEED_ALARM_REPEAT_MINUTES_KEY,
  alarmOpenMinutes: FEED_ALARM_OPEN_MINUTES_KEY,
  silentMinutes: FEED_SILENT_MINUTES_KEY,
  pushPerKeyMinute: FEED_PUSH_PER_KEY_MINUTE_KEY,
  pushPerAddressMinute: FEED_PUSH_PER_ADDRESS_MINUTE_KEY,
};

/** The rows the seed writes once and never overwrites; the purge's hour rides the job clock's own list. */
export const PLANT_FEED_SETTINGS: readonly { key: string; value: string | number }[] = (Object.keys(FEED_SETTING_KEYS) as (keyof FeedSettings)[]).map((field) => ({ key: FEED_SETTING_KEYS[field], value: FEED_SETTING_DEFAULTS[field] }));

/** The settings as stored, each falling back to its seed where a row is missing or holds the wrong kind of value. */
export function feedSettingsFrom(read: (key: string) => unknown): FeedSettings {
  const number = (field: Exclude<keyof FeedSettings, 'noiseMode'>, min: number): number => {
    const value = read(FEED_SETTING_KEYS[field]);
    return typeof value === 'number' && Number.isFinite(value) && value >= min ? value : FEED_SETTING_DEFAULTS[field];
  };
  const mode = read(FEED_NOISE_MODE_KEY);
  return {
    noiseMode: typeof mode === 'string' && (FEED_NOISE_MODES as readonly string[]).includes(mode) ? (mode as FeedNoiseMode) : FEED_SETTING_DEFAULTS.noiseMode,
    noiseShare: number('noiseShare', 0),
    heartbeatMinutes: number('heartbeatMinutes', 0),
    keepDays: number('keepDays', 1),
    clockSkewWarnMinutes: number('clockSkewWarnMinutes', 0),
    alarmRepeatCount: number('alarmRepeatCount', 1),
    alarmRepeatMinutes: number('alarmRepeatMinutes', 1),
    alarmOpenMinutes: number('alarmOpenMinutes', 1),
    silentMinutes: number('silentMinutes', 1),
    pushPerKeyMinute: number('pushPerKeyMinute', 1),
    pushPerAddressMinute: number('pushPerAddressMinute', 1),
  };
}

// ---- The contract (decision 12502) ----

export interface FeedReadingInput {
  tag: string;
  value: number;
  unit: string | null;
  at: Date;
  clock: Date | null;
}

export type FeedFaultStateInput = 'On' | 'Off';

export interface FeedFaultInput {
  tag: string;
  code: string;
  text: string;
  state: FeedFaultStateInput;
  at: Date;
  clock: Date | null;
}

export interface FeedStatusInput {
  state: 'online' | 'offline';
  at: Date | null;
  clock: Date | null;
}

export interface FeedEventInput {
  event: 'reconnected';
  lostAt: Date;
  restoredAt: Date;
  clock: Date | null;
}

export type FeedParse<T> = { ok: true; entries: T[] } | { ok: false; problem: string };

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

function instant(value: unknown): Date | null {
  if (typeof value !== 'string' || value.length > 40) return null;
  // ISO 8601 with a zone, as the controller stamps it; a bare local time names no instant.
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.test(value)) return null;
  const at = new Date(value);
  // 🔒 Year 0000 and before read as a time here but the database refuses them, which would lose a message the client was told was received (T3811).
  return Number.isNaN(at.getTime()) || at.getUTCFullYear() < 1 ? null : at;
}

function shortText(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  // 🔒 A NUL character cannot be stored in a text column, so a name holding one is refused with the message and never acknowledged (T3811).
  return trimmed.length > 0 && trimmed.length <= max && !trimmed.includes('\u0000') ? trimmed : null;
}

function entriesOf(body: unknown): unknown[] | string {
  const list = Array.isArray(body) ? body : [body];
  if (list.length === 0) return 'A message carries at least one entry.';
  if (list.length > FEED_MAX_ENTRIES) return `A message carries at most ${FEED_MAX_ENTRIES} entries; this one carries ${list.length}.`;
  return list;
}

/** One reading or a bundle of them, each checked whole; the first entry that fails names itself and refuses the message. */
export function parseFeedReadings(body: unknown): FeedParse<FeedReadingInput> {
  const list = entriesOf(body);
  if (typeof list === 'string') return { ok: false, problem: list };
  const entries: FeedReadingInput[] = [];
  for (const [index, raw] of list.entries()) {
    const where = `Entry ${index + 1}`;
    if (!isObject(raw)) return { ok: false, problem: `${where} is not an object.` };
    const tag = shortText(raw.tag, FEED_MAX_TAG);
    if (!tag) return { ok: false, problem: `${where} names no tag, or one longer than ${FEED_MAX_TAG} characters, or one holding a NUL character.` };
    if (typeof raw.value !== 'number' || !Number.isFinite(raw.value)) return { ok: false, problem: `${where}, ${tag}: the value is not a number.` };
    const unit = raw.unit === undefined || raw.unit === null ? null : shortText(raw.unit, 40);
    if (raw.unit !== undefined && raw.unit !== null && unit === null) return { ok: false, problem: `${where}, ${tag}: the unit is not a short name.` };
    const at = instant(raw.at);
    if (!at) return { ok: false, problem: `${where}, ${tag}: at is not an ISO 8601 time with its zone.` };
    const clock = raw.clock === undefined || raw.clock === null ? null : instant(raw.clock);
    if (raw.clock !== undefined && raw.clock !== null && clock === null) return { ok: false, problem: `${where}, ${tag}: clock is not an ISO 8601 time with its zone.` };
    entries.push({ tag, value: raw.value, unit, at, clock });
  }
  return { ok: true, entries };
}

export function parseFeedFaults(body: unknown): FeedParse<FeedFaultInput> {
  const list = entriesOf(body);
  if (typeof list === 'string') return { ok: false, problem: list };
  const entries: FeedFaultInput[] = [];
  for (const [index, raw] of list.entries()) {
    const where = `Entry ${index + 1}`;
    if (!isObject(raw)) return { ok: false, problem: `${where} is not an object.` };
    const tag = shortText(raw.tag, FEED_MAX_TAG);
    if (!tag) return { ok: false, problem: `${where} names no tag, or one longer than ${FEED_MAX_TAG} characters, or one holding a NUL character.` };
    const code = shortText(raw.code, FEED_MAX_TAG);
    if (!code) return { ok: false, problem: `${where}, ${tag}: the fault names no code.` };
    // 🔒 A NUL character cannot be stored in a text column, so a fault text holding one is refused with the message as a tag is (T3827).
    if (typeof raw.text === 'string' && raw.text.includes('\u0000')) return { ok: false, problem: `${where}, ${tag}: the fault text holds a NUL character.` };
    const text = typeof raw.text === 'string' ? raw.text.trim().slice(0, FEED_MAX_TEXT) : '';
    const state = raw.state === 'on' ? 'On' : raw.state === 'off' ? 'Off' : null;
    if (!state) return { ok: false, problem: `${where}, ${tag}: state is on or off.` };
    const at = instant(raw.at);
    if (!at) return { ok: false, problem: `${where}, ${tag}: at is not an ISO 8601 time with its zone.` };
    const clock = raw.clock === undefined || raw.clock === null ? null : instant(raw.clock);
    if (raw.clock !== undefined && raw.clock !== null && clock === null) return { ok: false, problem: `${where}, ${tag}: clock is not an ISO 8601 time with its zone.` };
    entries.push({ tag, code, text: text || code, state, at, clock });
  }
  return { ok: true, entries };
}

/** The retained status message: the will's offline, or online on each connect (decision 12502). */
export function parseFeedStatus(body: unknown): FeedStatusInput | null {
  if (!isObject(body) || (body.state !== 'online' && body.state !== 'offline')) return null;
  return { state: body.state, at: instant(body.at), clock: instant(body.clock) };
}

/** The device's own outage report on reconnect (decision 12502). */
export function parseFeedEvent(body: unknown): FeedEventInput | null {
  if (!isObject(body) || body.event !== 'reconnected') return null;
  const lostAt = instant(body.lostAt);
  const restoredAt = instant(body.restoredAt);
  if (!lostAt || !restoredAt || restoredAt.getTime() < lostAt.getTime()) return null;
  return { event: 'reconnected', lostAt, restoredAt, clock: instant(body.clock) };
}

// ---- The reader's rules (decisions 12500, 12502, 12504) ----

/** The controller's clock less the receive time, in whole seconds; null where the message sent no clock. */
export function clockSkewSeconds(clock: Date | null, receivedAt: Date): number | null {
  return clock ? Math.round((clock.getTime() - receivedAt.getTime()) / 1000) : null;
}

/** Whether the clock difference reads amber on the card. */
export function clockSkewWarns(skewSeconds: number | null, warnMinutes: number): boolean {
  return skewSeconds !== null && Math.abs(skewSeconds) > warnMinutes * 60;
}

/**
 * The time window a reading's event time is held to (decision 12500): corrected
 * by the controller's clock difference, it is no later than the receive time plus
 * the warning minutes and no older than the keep days. A late bundle hours old
 * passes; one dated tomorrow by a wrong clock does not.
 */
export function feedTimeProblem(eventAt: Date, controllerClockAt: Date | null, receivedAt: Date, settings: Pick<FeedSettings, 'clockSkewWarnMinutes' | 'keepDays'>): string | null {
  const skewMs = controllerClockAt ? controllerClockAt.getTime() - receivedAt.getTime() : 0;
  const corrected = eventAt.getTime() - skewMs;
  if (corrected > receivedAt.getTime() + settings.clockSkewWarnMinutes * 60_000) return 'The event time is later than the time it arrived, past the clock warning.';
  if (corrected < receivedAt.getTime() - settings.keepDays * 86_400_000) return 'The event time is older than the days the feed keeps.';
  return null;
}

/** The last reading of the same tag kept in event order, which the noise rule compares against. */
export interface KeptReading {
  value: number;
  eventAt: Date;
}

/**
 * The noise rule (decision 12504): null where the reading is kept, else why it
 * is dropped. 🔨 The heartbeat keeps one reading every so many minutes of event
 * time whatever it moved; a fault never comes through here.
 */
export function noiseDrop(value: number, eventAt: Date, last: KeptReading | null, settings: Pick<FeedSettings, 'noiseMode' | 'noiseShare' | 'heartbeatMinutes'>): string | null {
  if (!last || settings.noiseMode === 'Keep') return null;
  if (settings.heartbeatMinutes > 0 && eventAt.getTime() - last.eventAt.getTime() >= settings.heartbeatMinutes * 60_000) return null;
  if (settings.noiseMode === 'DropEqual') return value === last.value ? 'Equal to the last reading kept.' : null;
  const band = Math.abs(last.value) * (settings.noiseShare / 100);
  return Math.abs(value - last.value) <= band ? `Within ${settings.noiseShare} percent of the last reading kept.` : null;
}

/**
 * The alarm rule (decision 12505): whether an open fault raises its To-Do, from
 * the number of times its code turned on within the repeat minutes and how long
 * it has stood open.
 */
export function faultAlarmDue(onsInWindow: number, openedAt: Date, now: Date, settings: Pick<FeedSettings, 'alarmRepeatCount' | 'alarmOpenMinutes'>): boolean {
  return onsInWindow >= settings.alarmRepeatCount || now.getTime() - openedAt.getTime() >= settings.alarmOpenMinutes * 60_000;
}

/** Whether a connector has been quiet past the silent minutes (decision 12502). */
export function feedSilent(lastHeardAt: Date | null, now: Date, silentMinutes: number): boolean {
  return lastHeardAt !== null && now.getTime() - lastHeardAt.getTime() >= silentMinutes * 60_000;
}

// ---- The two To-Do kinds (decisions 12502, 12505) ----

export const ASSET_FAULT_ALARM_KIND = 'AssetFaultAlarm' as const;
export const PLANT_FEED_SILENT_KIND = 'PlantFeedSilent' as const;
/** The alarm opens the node's page, the silence the Site page; the source names each. */
export const ASSET_FAULT_ALARM_SOURCE = 'AssetNode';
export const PLANT_FEED_SILENT_SOURCE = 'SiteRecord';

// ---- What the Site page's Plant Feed tile reads (decision 12503) ----

export const FEED_CONNECTOR_STATES = ['Never', 'Online', 'Offline', 'Silent'] as const;
export type FeedConnectorState = (typeof FEED_CONNECTOR_STATES)[number];
export const FEED_OUTAGE_SOURCES = ['Device', 'Broker', 'Silence'] as const;
export type FeedOutageSource = (typeof FEED_OUTAGE_SOURCES)[number];
export const FEED_TAG_KINDS = ['Reading', 'Fault'] as const;
export type FeedTagKind = (typeof FEED_TAG_KINDS)[number];

export interface PlantFeedOutageAnswer {
  id: string;
  lostAt: string;
  restoredAt: string | null;
  source: FeedOutageSource;
}

export interface PlantFeedTagMapAnswer {
  id: string;
  tag: string;
  kind: FeedTagKind;
  assetNodeId: string;
  assetNodeName: string;
  attributeTypeId: string | null;
  attributeName: string | null;
  unitId: string | null;
  unitAbbreviation: string | null;
}

export interface PlantFeedConnectorAnswer {
  id: string;
  siteKey: string;
  brokerUser: string;
  state: FeedConnectorState;
  stateSince: string | null;
  lastHeardAt: string | null;
  clockSkewSeconds: number | null;
  clockSkewWarns: boolean;
  readingsToday: number;
  faultsToday: number;
  createdAt: string;
}

/** A node the Map Tag window offers: the site's tree, by name, with whether it is a Component an attribute can be written on. */
export interface PlantFeedNodeOption {
  id: string;
  name: string;
  isComponent: boolean;
}

export interface PlantFeedAttributeOption {
  id: string;
  name: string;
  units: { id: string; abbreviation: string; name: string }[];
}

export interface PlantFeedAnswer {
  connector: PlantFeedConnectorAnswer | null;
  maps: PlantFeedTagMapAnswer[];
  /** Import Devices: the tags the feed has seen that no map names, with how often and when last. */
  unmappedTags: { tag: string; kind: FeedTagKind; count: number; lastAt: string }[];
  outages: PlantFeedOutageAnswer[];
  nodes: PlantFeedNodeOption[];
  attributeTypes: PlantFeedAttributeOption[];
  clockSkewWarnMinutes: number;
  /** The broker's host and port as the device dials them, where the stack names one. */
  brokerAddress: string | null;
}

/** What Connect this Site answers once; neither secret is ever answered again (decision 12503). */
export interface PlantFeedConnectAnswer {
  siteKey: string;
  brokerUser: string;
  brokerPassword: string;
  httpKey: string;
}
